/* eslint-disable react/no-unstable-nested-components */
import cx from 'classnames';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownTextProps {
  text: string;
  className?: string;
}

/**
 * Renders chat-bubble markdown. Scoped to the inline + block features the
 * assistant actually emits — paragraphs, bold/italic, inline code, code
 * blocks, links, and lists. Headings are intentionally styled the same as
 * paragraphs to keep the bubble compact.
 */
export function MarkdownText({ text, className }: MarkdownTextProps) {
  return (
    <div
      className={cx(
        'tw-text-15',
        'tw-text-label-primary',
        'tw-space-y-8',
        className,
      )}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="tw-m-0">{children}</p>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cx('tw-text-orange-80', 'tw-underline')}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="tw-font-medium">{children}</strong>
          ),
          em: ({ children }) => <em className="tw-italic">{children}</em>,
          // Block code: render the `<pre>` itself as the code window so
          // the styling doesn't depend on whether the fenced block has a
          // language tag.
          pre: ({ children }) => (
            <pre
              className={cx(
                'tw-m-0',
                'tw-px-12',
                'tw-py-8',
                'tw-rounded-6',
                'tw-bg-fill-secondary',
                'tw-text-13',
                'tw-font-mono',
                'tw-whitespace-pre-wrap',
                'tw-break-words',
                'tw-overflow-x-auto',
              )}
            >
              {children}
            </pre>
          ),
          // Inline code: a pill. When this <code> is nested inside a <pre>
          // (block code), the parent <pre>'s styling already covers it, so
          // we strip the pill background. react-markdown sets a
          // `language-*` class on labeled fenced blocks; for unlabeled
          // blocks (plain ``` ... ```) we fall back to detecting newlines
          // in the rendered content.
          code: ({ children, className: codeClassName }) => {
            const childText = typeof children === 'string' ? children : '';

            const isInsidePre =
              (typeof codeClassName === 'string' &&
                codeClassName.startsWith('language-')) ||
              childText.includes('\n');

            if (isInsidePre) {
              return <code>{children}</code>;
            }

            return (
              <code
                className={cx(
                  'tw-px-4',
                  'tw-py-2',
                  'tw-rounded-4',
                  'tw-bg-fill-secondary',
                  'tw-text-13',
                  'tw-font-mono',
                )}
              >
                {children}
              </code>
            );
          },
          ul: ({ children }) => (
            <ul className={cx('tw-m-0', 'tw-pl-20', 'tw-list-disc')}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={cx('tw-m-0', 'tw-pl-20', 'tw-list-decimal')}>
              {children}
            </ol>
          ),
          li: ({ children }) => <li>{children}</li>,
          // GFM tables. Wrap in a scroll container so wide tables
          // don't blow out the chat bubble — narrow tables still
          // sit naturally inside the bubble width.
          table: ({ children }) => (
            <div
              className={cx(
                'tw-overflow-x-auto',
                'tw-rounded-6',
                'tw-border',
                'tw-border-divider-primary',
              )}
            >
              <table
                className={cx('tw-w-full', 'tw-text-13', 'tw-border-collapse')}
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="tw-bg-fill-secondary">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="tw-border-b tw-border-divider-primary last:tw-border-b-0">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th
              className={cx(
                'tw-px-8',
                'tw-py-6',
                'tw-text-left',
                'tw-font-medium',
              )}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={cx('tw-px-8', 'tw-py-6', 'tw-align-top')}>
              {children}
            </td>
          ),
        }}
      >
        {text}
      </Markdown>
    </div>
  );
}
